import type {
  BackendProductUpsertOperation,
  BackendProductUpsertOperationNameEnum,
  ObpSection,
  SelectedProduct,
} from "@credit-acceptance/step6-bff-client";

import { ContractToBackendNamesMap } from "./forms/constants";

type ObpProductValues = Omit<BackendProductUpsertOperation, "sellingPrice" | "termMonths" | "termMiles" | "formId"> & {
  sellingPrice: number | null;
  termMonths: number | null;
  termMiles: number | null;
  formId: number | null;
};

export interface ObpFormValues {
  products: ObpProductValues[];
}

// eslint-disable-next-line unicorn/no-null -- RHF requires null for cleared numbers; undefined restores the default value.
const EMPTY_NUMBER = null;

/**
 * Maps the API date format and nullable numbers into controlled form values.
 * @param obp The dashboard OBP section.
 * @returns {ObpFormValues} The editable products.
 */
export function toObpFormValues(obp?: ObpSection): ObpFormValues {
  return {
    products: (obp?.products ?? []).map((product) => ({
      ...product,
      sellingPrice: product.sellingPrice ?? EMPTY_NUMBER,
      termMonths: product.termMonths ?? EMPTY_NUMBER,
      termMiles: product.termMiles ?? EMPTY_NUMBER,
      formId: product.formId ?? EMPTY_NUMBER,
      // The BFF uses MM-dd-yyyy; the shared date input speaks ISO.
      effectiveDate: product.effectiveDate?.replace(/^(\d{2})-(\d{2})-(\d{4})$/, "$3-$1-$2") ?? "",
    })),
  };
}

/**
 * Serializes product values without the response-only validations.
 * @param values The current OBP form values.
 * @returns {ObpSection} The OBP contribution to the v2 save request.
 */
export function toObpSaveRequest(values: ObpFormValues): ObpSection {
  return {
    products: values.products.map((product) => ({
      ...product,
      sellingPrice: product.sellingPrice ?? undefined,
      termMonths: product.termMonths ?? undefined,
      termMiles: product.termMiles ?? undefined,
      formId: product.formId ?? undefined,
      effectiveDate: product.effectiveDate?.replace(/^(\d{4})-(\d{2})-(\d{2})$/, "$2-$3-$1") || undefined,
    })),
  };
}

/**
 * Matches an `additionalProducts.contractObp` entry to a rendered OBP product, by the same
 * lowercase-name convention `other-backend-products`' `ContractToBackendNamesMap` uses
 * (`selectedProductName` is free text off the Contract Worksheet Service, not the BFF's enum).
 * @param contractObp The dashboard's `additionalProducts.contractObp` list, absent when that
 * read failed (distinct from "present but no entry for this product").
 * @param productName The rendered product's official (BFF) name.
 * @returns {SelectedProduct | undefined} The matching contract/RISC entry, if any.
 */
export function findContractObpProduct(
  contractObp: SelectedProduct[] | undefined,
  productName: BackendProductUpsertOperationNameEnum
): SelectedProduct | undefined {
  return contractObp?.find(
    (entry) => ContractToBackendNamesMap[entry.selectedProductName?.toLowerCase() ?? ""] === productName
  );
}
